import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Dialog } from '~/ui/core/dialog/Dialog';
import { IconCrossSmall, IconSettings } from '~/ui/core/icons';

import { JobLevelGenerateWithAiSettingsMenuItem } from '../job-level-settings/JobLevelGenerateWithAiSettingsMenuItem';
import { Spinner } from './Spinner';

export type DefineTemplateOption = 'default' | 'custom';

type DefineJobProfileTemplateDialogProps = {
    readonly isActivating: boolean;
    readonly onClose: () => void;
    readonly onContinue: (option: DefineTemplateOption) => void;
    readonly onNavigateToSettings: () => void;
    readonly open: boolean;
};

export const DefineJobProfileTemplateDialog: FunctionComponent<DefineJobProfileTemplateDialogProps> = ({
    isActivating,
    onClose,
    onContinue,
    onNavigateToSettings,
    open,
}) => {
    const translate = useTranslate();
    const [selectedOption, setSelectedOption] = useState<DefineTemplateOption>('default');

    return (
        <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <Dialog.Portal>
                <Dialog.Backdrop />
                <Dialog.Content className="flex w-[31.25rem] flex-col p-3xl">
                    <div className="flex flex-row items-start justify-between">
                        <Dialog.Title className="m-0 mb-m text-base font-medium text-black-900">
                            {translate('pages.job-architecture-v2.define-job-profile-template-dialog.title')}
                        </Dialog.Title>
                        <Button disabled={isActivating} iconOnly={true} size="sm" variant="tertiary" onClick={onClose}>
                            <IconCrossSmall />
                        </Button>
                    </div>
                    <div className="flex flex-col gap-xl">
                        <JobLevelGenerateWithAiSettingsMenuItem
                            checked={selectedOption === 'default'}
                            subtitle={translate(
                                'pages.job-architecture-v2.define-job-profile-template-dialog.default-option-subtitle',
                            )}
                            title={translate(
                                'pages.job-architecture-v2.define-job-profile-template-dialog.default-option-title',
                            )}
                            onClick={() => setSelectedOption('default')}
                        />
                        <JobLevelGenerateWithAiSettingsMenuItem
                            checked={selectedOption === 'custom'}
                            subSubtitle={translate(
                                'pages.job-architecture-v2.define-job-profile-template-dialog.requires-setup',
                            )}
                            subtitle={translate(
                                'pages.job-architecture-v2.define-job-profile-template-dialog.custom-option-subtitle',
                            )}
                            title={translate(
                                'pages.job-architecture-v2.define-job-profile-template-dialog.custom-option-title',
                            )}
                            onClick={() => setSelectedOption('custom')}
                        />
                    </div>
                    <div className="mt-xl flex items-center justify-between gap-m border-t border-gray-300 pt-xl">
                        <Button iconPosition="leading" variant="tertiary" onClick={onNavigateToSettings}>
                            <IconSettings size={16} />
                            {translate('common.settings')}
                        </Button>
                        <div className="flex gap-m">
                            <Button disabled={isActivating} variant="tertiary" onClick={onClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button
                                disabled={isActivating}
                                variant="primary"
                                onClick={() => onContinue(selectedOption)}
                            >
                                {isActivating ? <Spinner /> : null}
                                {translate(
                                    selectedOption === 'custom'
                                        ? 'pages.job-architecture-v2.define-job-profile-template-dialog.setup-template'
                                        : 'pages.job-architecture-v2.define-job-profile-template-dialog.continue',
                                )}
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
